import React, { useRef, useState, useEffect } from 'react';
import { Container, Row } from 'reactstrap';
import PropTypes from 'prop-types';
import { StyleSheet, css } from 'aphrodite';
import { layout, mediaQuery } from '@lumoslabs/react_lux';
import { throttle, debounce } from 'lodash';

import GameCard from './GameCard';

import determineVisibleCards from './determineVisibleCards';

const styles = StyleSheet.create({
  container: {
    paddingBottom: 0,
    paddingTop: 0,
    maxWidth: 1400,
    padding: 0,
    [mediaQuery.maxWidth.desktop]: {
      padding: 0
    }
  },
  row: {
    [mediaQuery.maxWidth.desktop]: {
      justifyContent: 'flex-start',
      flexWrap: 'nowrap',
      overflowX: 'scroll',
      overflowY: 'hidden',
      width: '100vw',
      '-webkit-overflow-scrolling': 'touch'
    }
  }
});

function GameCardCollection({ games, wrapperStyles, locale }) {
  // visible cards added to state so child card component can fade in as they scroll into view
  const [visibleCards, setVisibleCards] = useState([]);
  const rowWrapper = useRef(null);

  function handleScroll() {
    const row = rowWrapper.current.children[0];
    const cards = Array.from(row.children);
    determineVisibleCards(cards, row, setVisibleCards);
  }

  // throttle must be used as a ref or it doesn't work
  // (because successive renders don't point to the same function)
  const scrollEffect = useRef(
    throttle(() => {
      handleScroll();
    }, 150)
  );

  // debounce must be used as a ref or it doesn't work
  // (because successive renders don't point to the same function)
  // Function is debounced so effect loads on page load
  const loadEffect = useRef(
    debounce(() => {
      handleScroll();
    }, 150)
  );

  useEffect(
    () => {
      loadEffect.current();
    },
    [visibleCards.current]
  );

  return (
    <Container className={css(layout.defaultContainerProperties, styles.container)}>
      <div
        // wrapping the Row component because reactstrap doesn't currently support forwardRef
        // https://github.com/reactstrap/reactstrap/pull/1356
        // https://github.com/reactstrap/reactstrap/issues/1082
        ref={rowWrapper}
      >
        <Row
          noGutters
          className={css(wrapperStyles, layout.centerHorizontal, styles.row)}
          onScroll={() => scrollEffect.current()}
        >
          {games.map(game => (
            <GameCard locale={locale} key={game.name} game={game} visibleCards={visibleCards} />
          ))}
        </Row>
      </div>
    </Container>
  );
}

GameCardCollection.propTypes = {
  games: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      description: PropTypes.string
    })
  ).isRequired,
  wrapperStyles: PropTypes.arrayOf(PropTypes.object),
  locale: PropTypes.string.isRequired
};

GameCardCollection.defaultProps = {
  wrapperStyles: []
};

export default GameCardCollection;
